import type { AxiosResponse } from "axios"
import type {
  EntityPendingChangeResponse,
  PendingChangeDecisionResponse,
  PendingChangesListResponse,
} from "../../../../shared/apiTypes"
import api from "../api"
import {
  GET_PENDING_CHANGES,
  APPROVE_PENDING_CHANGE,
  REJECT_PENDING_CHANGE,
  CLEAR_CHANGES_MESSAGE,
  GET_ENTITY_PENDING_CHANGE,
  CLEAR_ENTITY_PENDING_CHANGE,
} from "./pendingChangesTypes"
import { GET_ERRORS } from "../errorServices/errorTypes"
import { errorPayload } from "../caughtError";

export const clearChangesMessage = () => (dispatch) => {
  dispatch({ type: CLEAR_CHANGES_MESSAGE })
}

export const getPendingChanges = (filters: { entityType?: string; status?: string } = {}) => async (dispatch) => {
  try {
    const params = new URLSearchParams()
    if (filters.entityType) params.append("entityType", filters.entityType)
    if (filters.status) params.append("status", filters.status)
    const query = params.toString() ? `?${params.toString()}` : ""
    const res: AxiosResponse<PendingChangesListResponse> = await api.get(
      `/api/pending-changes${query}`
    )
    dispatch({ type: GET_PENDING_CHANGES, payload: res.data })
  } catch (err) {
    // SCRUM-1312: was the hardcoded "Failed to load pending changes", which the hub
    // shows in place of the server's own reason. Same guard as the other reads in
    // this services tree (historiesActions, productActions, tagActions).
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    })
  }
}

export const approvePendingChange = (id) => async (dispatch) => {
  try {
    const res: AxiosResponse<PendingChangeDecisionResponse> = await api.put(
      `/api/pending-changes/${id}/approve`
    )
    dispatch({ type: APPROVE_PENDING_CHANGE, payload: res.data })
  } catch (err) {
    // SCRUM-1312: was the hardcoded "Failed to approve change". The reviewer needs the
    // server's reason here — already reviewed, entity gone, validation refused.
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    })
  }
}

/**
 * SCRUM-1838: approve every pending row of one changeset. Rows whose current
 * value no longer matches the changeset's `expect` are refused by the server
 * and stay pending; their reasons go to the error hub, and the list is
 * re-read either way so the screen shows what was applied.
 */
export const approveChangeset = (name: string) => async (dispatch) => {
  try {
    const res: AxiosResponse<{ applied: string[]; refused: { id: string; index: number; msg: string }[] }> = await api.put(
      `/api/pending-changes/changeset/${encodeURIComponent(name)}/approve`
    )
    if (res.data?.refused?.length) {
      dispatch({
        type: GET_ERRORS,
        payload: {
          errors: res.data.refused.map((r) => ({ msg: `Row ${r.index}: ${r.msg}` })),
        },
      })
    }
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    })
  }
  return dispatch(getPendingChanges())
}

export const rejectPendingChange = (id) => async (dispatch) => {
  try {
    const res: AxiosResponse<PendingChangeDecisionResponse> = await api.put(
      `/api/pending-changes/${id}/reject`
    )
    dispatch({ type: REJECT_PENDING_CHANGE, payload: res.data })
  } catch (err) {
    // SCRUM-1312: was the hardcoded "Failed to reject change".
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    })
  }
}

export const getEntityPendingChange = (entityType, entityId) => async (dispatch) => {
  try {
    const res: AxiosResponse<EntityPendingChangeResponse> = await api.get(
      `/api/pending-changes/entity/${entityType}/${entityId}`
    )
    dispatch({ type: GET_ENTITY_PENDING_CHANGE, payload: res.data })
  } catch {
    dispatch({ type: GET_ENTITY_PENDING_CHANGE, payload: null })
  }
}

export const clearEntityPendingChange = () => (dispatch) => {
  dispatch({ type: CLEAR_ENTITY_PENDING_CHANGE })
}
